import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { guardarExamenLaboratorioService } from "../../../Service/Service.examen";
import { CITA_FINALIZADA_SENTINEL } from "../../../utils/citaFinalizadaAlert";
import HCModal from "../HCModal";
import { DateBadge, ExamenPicker, Field, OrderBadge, Section, Segmented } from "../HCPiezas";
import { hcAviso, hcError, hcExito } from "../hcSwal";
import ModalRutinas from "./ModalRutinas";
import TabDiagnosticos from "./TabDiagnosticos";

/* ============================================================================
   Orden de laboratorio — migrado a HCModal.

   Payload y endpoint idénticos: POST /examen_registro_cita
   { idcita, IdCategoriaExamen, idpaciente, fecha, numero_orden, sala,
     IdExamenTipo[] }. Laboratorio NO tiene motivo ni resumen clínico: la tabla
   `examen_cita_laboratorio` no tiene esas columnas.

   Se conserva tal cual lo que hacía el modal viejo:
   · `IdCategoriaExamen` sale de `tiposEstudio[0].categoria_id` (no del prop,
     que es lo que usa imagen).
   · La lista de exámenes se filtra por `tiposEstudio[0].tipo`.
   · Las RUTINAS son el atajo de verdad de laboratorio: cargar una rutina
     reemplaza `IdExamenTipo` con sus exámenes, y guardar una manda los
     marcados a POST create/rutina. Misma semántica, otro envoltorio.

   Nota de lo que NO se pierde: los interruptores de "Estudios" del modal
   viejo eran decorativos -- `handlerCheckoutTipoEstudio` hacía `return true` y
   la lista salía siempre de `tiposEstudio[0]`. Por eso aquí no hay selector de
   categoría: reproduce el comportamiento real, no el aparente.

   Cambios acordados: `sala` con placeholder en vez del literal "n/a" (columna
   nullable; se retira la validación del front que la exigía) y `numero_orden`
   de solo lectura.
   ========================================================================== */

const PRIORIDADES = [
  { value: "rutina", label: "Rutina" },
  { value: "control", label: "Control" },
  { value: "urgente", label: "Urgente", peligro: true },
];

const FORM_VACIO = {
  idcita: 0,
  IdCategoriaExamen: 0,
  idpaciente: 0,
  fecha: moment().format("YYYY-MM-DD"),
  numero_orden: "",
  sala: "",
  IdExamenTipo: [],
};

export default function ModalOrdenLaboratorio({
  show,
  onHide,
  onSaveSuccess,
  cita,
  paciente,
  tiposEstudio = [],
  examenes = [],
  ListaCitaPaciente,
}) {
  const [tab, setTab] = useState("orden");
  const [form, setForm] = useState(FORM_VACIO);
  const [prioridad, setPrioridad] = useState("rutina"); // no persiste: no hay columna
  const [loading, setLoading] = useState(false);
  const [intentado, setIntentado] = useState(false);
  const [rutinaModo, setRutinaModo] = useState(null); // "cargar" | "guardar" | null
  const [rutinaCargada, setRutinaCargada] = useState(null);
  const examenesRef = useRef(null);

  const categoria = tiposEstudio[0];

  useEffect(() => {
    if (!show || !cita) return;
    setForm({
      ...FORM_VACIO,
      idcita: cita.idcita,
      idpaciente: cita.pacientes?.idpaciente,
      // Igual que el modal viejo: la categoría sale del catálogo, no del prop.
      IdCategoriaExamen: categoria?.categoria_id ?? 0,
      numero_orden: `LAB-${cita.idcita}-CECOLON`,
      fecha: moment().format("YYYY-MM-DD"),
    });
    setPrioridad("rutina");
    setRutinaCargada(null);
    setIntentado(false);
    setTab("orden");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, cita, categoria?.categoria_id]);

  const examenesCategoria = useMemo(() => {
    if (!categoria) return [];
    return examenes.filter((e) => e.tipo === categoria.tipo);
  }, [examenes, categoria]);

  // Los objetos completos de lo marcado — los necesita "guardar como rutina",
  // que manda descripciones e ids, no solo ids.
  const seleccionados = useMemo(
    () => examenesCategoria.filter((e) => form.IdExamenTipo.includes(e.IdExamenTipo)),
    [examenesCategoria, form.IdExamenTipo]
  );

  const alternarExamen = (id) => {
    setRutinaCargada(null); // tocar la selección a mano deja de ser "la rutina X"
    setForm((f) => {
      const set = new Set(f.IdExamenTipo);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return { ...f, IdExamenTipo: [...set] };
    });
  };

  /* Misma semántica que `handleRutinaSelect` del modal viejo (REEMPLAZA la
     selección), pero con un filtro que allí faltaba.

     Se intersecta con lo que este paciente puede pedir de verdad (la lista de
     la categoría, que ya incluye su tarifario Y el particular) y se DICE qué
     quedó fuera, en vez de mandar ids que el catálogo no puede valorar.

     Antes esto además evitaba un 500: el backend hacía
     `precio: examenPrecio[0].precio` sin respaldo y un examen sin tarifario
     para ese seguro lanzaba, tumbando la orden entera. Eso ya está arreglado
     en el backend (TARIFARIO-RESPALDO-PARTICULAR-01), así que aquí el filtro
     queda solo como aviso honesto de lo que el catálogo no tiene. */
  const cargarRutina = (rutina) => {
    const ids = (rutina.detalles || []).map((d) => d.ExamenTipo?.IdExamenTipo).filter(Boolean);
    const disponibles = new Set(examenesCategoria.map((e) => e.IdExamenTipo));
    const aplicables = ids.filter((id) => disponibles.has(id));
    const fuera = (rutina.detalles || [])
      .filter((d) => d.ExamenTipo?.IdExamenTipo && !disponibles.has(d.ExamenTipo.IdExamenTipo))
      .map((d) => d.ExamenTipo.descripcion)
      .filter(Boolean);

    setForm((f) => ({ ...f, IdExamenTipo: aplicables }));
    setRutinaCargada({ nombre: rutina.nombre, n: aplicables.length, fuera });

    if (aplicables.length === 0) {
      hcError(
        "Ningún examen de esta rutina está disponible",
        `«${rutina.nombre}» no tiene exámenes con tarifario, ni del seguro del paciente ni particular. Revisa el catálogo.`
      );
      return;
    }
    if (fuera.length > 0) {
      hcAviso(
        `Rutina cargada · ${aplicables.length} de ${ids.length}`,
        `Quedaron fuera por no tener tarifario (ni del seguro ni particular): ${fuera.join(", ")}.`
      );
      return;
    }
    hcExito(`Rutina «${rutina.nombre}» cargada`, `${aplicables.length} examen(es) seleccionado(s).`);
  };

  const faltantes = useMemo(() => {
    const f = [];
    if (form.IdExamenTipo.length === 0) f.push("Exámenes");
    if (!form.fecha) f.push("Fecha");
    return f;
  }, [form]);

  const guardar = async () => {
    setIntentado(true);
    if (faltantes.length > 0) {
      examenesRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setLoading(true);
    const data = await guardarExamenLaboratorioService(form);
    if (data === CITA_FINALIZADA_SENTINEL) {
      setLoading(false);
      return;
    }
    if (!data || data.success === false) {
      hcError("No se pudo enviar la orden", "Intenta de nuevo en unos segundos.");
      setLoading(false);
      return;
    }
    setLoading(false);
    hcExito("Orden de laboratorio enviada", `${form.IdExamenTipo.length} examen(es).`);
    if (onSaveSuccess) await onSaveSuccess();
    cerrar();
  };

  const cerrar = () => {
    setForm(FORM_VACIO);
    setRutinaCargada(null);
    onHide && onHide();
  };

  const diagnosticos = cita?.diagnostico_cita_paciente || [];
  const subtitulo = [
    paciente?.nombres_completos || cita?.pacientes?.nombres_completos,
    paciente?.edad ? `${paciente.edad} a` : null,
    cita?.idcita ? `Cita ${cita.idcita}` : null,
    [cita?.doctor?.prefijo, cita?.doctor?.nombre].filter(Boolean).join(" ") || null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <>
      <HCModal
        isOpen={!!show}
        size="lg"
        title="Orden de laboratorio"
        subtitle={subtitulo}
        meta={[
          <DateBadge key="f" value={moment(form.fecha).format("DD/MM/YYYY")} label="Fecha de la orden" />,
          <OrderBadge key="o" code={form.numero_orden} />,
        ]}
        tabs={[
          { key: "orden", label: "Orden" },
          { key: "diagnostico", label: "Diagnóstico", count: diagnosticos.length },
        ]}
        activeTab={tab}
        onTabChange={setTab}
        validation={{ missing: faltantes }}
        primaryLabel="Enviar orden"
        onPrimary={guardar}
        primaryLoading={loading}
        onClose={cerrar}
        onCancel={cerrar}
        hayCambiosSinGuardar={form.IdExamenTipo.length > 0}
      >
        {tab === "orden" ? (
          <>
            <Section
              title="1 · Exámenes solicitados"
              hint={categoria ? categoria.tipo : "Sin catálogo de laboratorio"}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap", marginBottom: 10 }}>
                <button type="button" className="hcbtn" onClick={() => setRutinaModo("cargar")}>
                  Usar una rutina
                </button>
                <button
                  type="button"
                  className="hcbtn"
                  disabled={seleccionados.length === 0}
                  title={seleccionados.length === 0 ? "Marca primero los exámenes" : undefined}
                  onClick={() => setRutinaModo("guardar")}
                >
                  Guardar selección como rutina
                </button>
                {rutinaCargada && (
                  <span className="hcm__badge">
                    Rutina «{rutinaCargada.nombre}» · {rutinaCargada.n} examen(es)
                  </span>
                )}
              </div>

              <div ref={examenesRef} tabIndex={-1}>
                {examenesCategoria.length === 0 ? (
                  <div className="hclist__vacio">
                    No hay exámenes de laboratorio disponibles para el seguro del paciente.
                  </div>
                ) : (
                  <ExamenPicker
                    items={examenesCategoria}
                    seleccionados={form.IdExamenTipo}
                    onToggle={alternarExamen}
                    placeholder="Buscar examen de laboratorio…"
                    vacio="Ningún examen coincide"
                  />
                )}
              </div>

              <div className="hcf__hint" style={{ marginTop: 6 }}>
                {form.IdExamenTipo.length > 0
                  ? `${form.IdExamenTipo.length} examen(es) seleccionado(s).`
                  : "Marca los exámenes, o carga una rutina para traerlos todos de una vez."}
              </div>
              {intentado && faltantes.includes("Exámenes") && (
                <div className="hcf__error" style={{ marginTop: 7 }} role="alert">
                  Selecciona al menos un examen.
                </div>
              )}
            </Section>

            <Section title="2 · Prioridad y logística">
              <div className="hcgrid hcgrid--3">
                <Field label="Prioridad" hint="Referencia para el servicio; no se guarda todavía.">
                  <Segmented ariaLabel="Prioridad" options={PRIORIDADES} value={prioridad} onChange={setPrioridad} />
                </Field>
                <Field label="Sala" htmlFor="hc-lab-sala">
                  <input
                    id="hc-lab-sala"
                    type="text"
                    placeholder="Sin asignar"
                    value={form.sala}
                    onChange={(e) => setForm({ ...form, sala: e.target.value })}
                  />
                </Field>
                <Field label="Fecha de la orden" required htmlFor="hc-lab-fecha">
                  <input
                    id="hc-lab-fecha"
                    type="date"
                    min={new Date().toISOString().split("T")[0]}
                    value={form.fecha}
                    onChange={(e) => setForm({ ...form, fecha: e.target.value })}
                  />
                </Field>
              </div>
            </Section>
          </>
        ) : (
          <TabDiagnosticos diagnosticos={diagnosticos} onRefrescar={ListaCitaPaciente} />
        )}
      </HCModal>

      <ModalRutinas
        modo={rutinaModo}
        isOpen={Boolean(rutinaModo)}
        onClose={() => setRutinaModo(null)}
        onCargar={cargarRutina}
        examenesSeleccionados={seleccionados}
      />
    </>
  );
}
